import { Link, useParams } from "react-router-dom";
import SiteBreadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticleById, getRelatedArticles } from "@/utils/content";
import { useSeo } from "@/hooks/useSeo";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);

  useSeo({
    title: article ? `${article.title} | 瓜田秘闻社` : "内容不存在 | 瓜田秘闻社",
    description: article ? article.summary : "这篇内容可能已被调整或移除，返回首页继续吃瓜吧。",
    keywords: article ? ["黑料吃瓜", "圈内八卦", ...article.tags].join(",") : "黑料吃瓜,圈内八卦",
    canonical: `/article/${id}`,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              headline: article.title,
              description: article.summary,
              datePublished: article.date,
              dateModified: article.date,
              author: { "@type": "Organization", name: article.author },
              publisher: { "@type": "Organization", name: "瓜田秘闻社" },
              articleSection: article.topicName,
              keywords: article.tags.join(","),
              inLanguage: "zh-CN",
              mainEntityOfPage: `https://sdbjdz.cn/article/${article.id}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://sdbjdz.cn/" },
                { "@type": "ListItem", position: 2, name: "吃瓜快讯", item: "https://sdbjdz.cn/news" },
                { "@type": "ListItem", position: 3, name: article.title, item: `https://sdbjdz.cn/article/${article.id}` },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto flex max-w-[900px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这口瓜好像被啃完了</h1>
        <p className="mt-3 text-sm text-foreground-600">这篇内容可能已被调整或移除。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i> 回到快讯列表
        </Link>
      </div>
    );
  }

  const headings = article.content
    .map((block, index) => ({ block, index }))
    .filter((item) => item.block.type === "h2");
  const related = getRelatedArticles(article, 3);

  return (
    <article className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[900px] px-4 py-8 md:px-6 md:py-10">
          <SiteBreadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "吃瓜快讯", to: "/news" },
              { label: article.topicName, to: `/news?topic=${article.topicId}` },
              { label: article.title },
            ]}
          />
          <span className="mt-5 inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
            {article.topicName}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i> {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i> 约 {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line"></i> {article.views} 阅读
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[900px] px-4 py-10 md:px-6">
        <div className="h-64 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
          <img src={article.image} alt={article.imageAlt} className="h-full w-full object-cover" />
        </div>

        {headings.length > 0 && (
          <nav className="mt-8 rounded-lg border border-background-200 bg-background-100/70 p-5" aria-label="文章目录">
            <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
              <i className="ri-list-check text-primary-500"></i> 本文目录
            </h2>
            <ol className="mt-3 space-y-2">
              {headings.map(({ block, index }, order) => (
                <li key={block.text}>
                  <a
                    href={`#sec-${index}`}
                    className="flex items-start gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    <span className="text-xs text-foreground-400">{order + 1}.</span>
                    {block.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="article-body mt-8 text-[15px] text-foreground-800">
          {article.content.map((block, index) =>
            block.type === "h2" ? (
              <h2 key={block.text} id={`sec-${index}`} className="scroll-mt-32">
                {block.text}
              </h2>
            ) : (
              <p key={`${block.text.slice(0, 8)}-${index}`}>{block.text}</p>
            )
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">相关标签：</span>
          {article.tags.map((tag) => (
            <Link
              key={tag}
              to={`/news?keyword=${encodeURIComponent(tag)}`}
              className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              #{tag}
            </Link>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-primary-900">
            <i className="ri-shield-check-line"></i> 内容说明
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-700">
            本文整理的是公开渠道流传的讨论与观点，涉及人物统一使用匿名代称，仅作娱乐性讨论，不构成对任何个人的事实指控。若有出入，欢迎通过
            <Link to="/contact" className="mx-1 text-primary-700 underline">
              联系我们
            </Link>
            提供更正线索。
          </p>
        </div>
      </div>

      <div className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续吃瓜</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}